import { Button } from '@base-ui/react/button';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { LAYOUT_LABELS } from '@/admin/sitecontent/conditions';
import { MutationError } from '@/admin/sitecontent/errors';
import { invalidateOnboardings } from '@/admin/sitecontent/invalidate';
import { OrderedList } from '@/admin/sitecontent/OrderedList';
import { StatusMessage } from '@/admin/sitecontent/StatusMessage';
import buttonStyles from '@/admin/styles/button.module.css';
import { Badge } from '@/admin/ui/Badge';
import { ConfirmDialog } from '@/admin/ui/ConfirmDialog';
import { OnboardingHeader, TabHeading } from '@/admin/ui/ContentNav';
import api from '@/api';

export const Route = createFileRoute('/admin/onboarding-slides/')({
  staticData: { minTier: 'ADMIN' },
  validateSearch: (s: Record<string, unknown>) => ({
    onboarding: Number(s.onboarding),
  }),
  component: OnboardingSlidesList,
});

function OnboardingSlidesList() {
  const { t } = useTranslation();
  const { onboarding } = Route.useSearch();
  const [deleting, setDeleting] = useState<number | null>(null);

  const { data, isLoading, error } = api.getOnboardingSequence.useQuery({
    path: { onboarding_id: onboarding },
  });
  const reorder = api.reorderOnboardingSlides.useMutation(undefined, {
    onSuccess: () => invalidateOnboardings(),
  });
  const remove = api.deleteOnboardingSlide.useMutation(undefined, {
    onSuccess: () => {
      invalidateOnboardings();
      setDeleting(null);
    },
  });

  return (
    <div>
      <OnboardingHeader onboardingId={onboarding} />
      <TabHeading
        action={
          <Link
            to="/admin/onboarding-slides/new/"
            search={{ onboarding }}
            className={buttonStyles.Button}
          >
            {t('admin.onboardingSlides.addSlide')}
          </Link>
        }
      >
        {t('admin.onboardingSlides.heading')}
      </TabHeading>
      {isLoading ? (
        <StatusMessage>{t('admin.onboardingSlides.loading')}</StatusMessage>
      ) : error || !data ? (
        <StatusMessage $error>
          {t('admin.onboardingSlides.errorLoading')}
        </StatusMessage>
      ) : data.slides.length === 0 ? (
        <StatusMessage>{t('admin.onboardingSlides.noSlides')}</StatusMessage>
      ) : (
        <OrderedList
          // A slide's id is its index, so a saved reorder leaves the id list
          // unchanged and OrderedList would never reset; keying on the slides'
          // content remounts it once the re-read arrives.
          key={JSON.stringify(data.slides)}
          items={data.slides.map((slide) => ({
            id: slide.slide_index,
            label: (
              <>
                <Index>{slide.slide_index}</Index>{' '}
                <Link
                  to="/admin/onboarding-slides/$onboardingId/$slideIndex/"
                  params={{
                    onboardingId: String(onboarding),
                    slideIndex: String(slide.slide_index),
                  }}
                >
                  {slide.title || t('admin.onboardingSlides.untitled')}
                </Link>{' '}
                <Badge $variant="neutral">
                  {t(LAYOUT_LABELS[slide.layout])}
                </Badge>{' '}
                {!slide.enabled && (
                  <Badge $variant="error">
                    {t('admin.onboardingSlides.disabled')}
                  </Badge>
                )}
              </>
            ),
            extra: (
              <Button
                className={buttonStyles.Button}
                aria-label={t('admin.onboardingSlides.deleteSlideLabel', {
                  index: slide.slide_index,
                  interpolation: { escapeValue: false },
                })}
                onClick={() => setDeleting(slide.slide_index)}
              >
                {t('admin.common.delete')}
              </Button>
            ),
          }))}
          rowLabel={(item) =>
            t('admin.onboardingSlides.slideN', {
              index: item.id,
              interpolation: { escapeValue: false },
            })
          }
          saving={reorder.isPending}
          error={reorder.error}
          errorArea="onboardingSlides"
          onSave={(indexes) =>
            reorder.mutate({
              path: { onboarding_id: onboarding },
              body: { slide_indexes: indexes },
            })
          }
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={t('admin.onboardingSlides.deleteTitle')}
        confirmLabel={t('admin.common.delete')}
        tone="danger"
        pending={remove.isPending}
        onConfirm={() =>
          deleting !== null &&
          remove.mutate({
            path: { onboarding_id: onboarding, slide_index: deleting },
          })
        }
        onCancel={() => setDeleting(null)}
      >
        <p>
          {t('admin.onboardingSlides.deleteBody', {
            index: deleting ?? '',
            interpolation: { escapeValue: false },
          })}
        </p>
        <MutationError area="onboardingSlides" error={remove.error} />
      </ConfirmDialog>
    </div>
  );
}

const Index = styled.span`
  color: var(--grey-600);
  display: inline-block;
  min-width: 1.5rem;
`;
